import React, { useEffect } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { getDateNowFunction, updateSessionDetail } from '@techstyle/redux-core';

import { grid } from '../../styles';
import { parseDate } from '../../utils/dates';
import { Component as Grid } from '../Grid';
import { Component as ProductCell } from '../ProductCell';

const trackedProductIds = new Set();
let productTrackingEntries = [];
const ProductGrid = ({
  asset = { id: '' },
  assetSlots,
  breakpoints = [grid.small, grid.medium, grid.large, grid.xlarge],
  children,
  className,
  filters,
  hidePromo = false,
  hoverable = true,
  isOnSearchGrid = false,
  loading = true,
  pSource = '',
  products,
  searchQuery,
  sizeLimit,
  sortBy,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const getNow = useSelector(state => getDateNowFunction(state));

  const productsToRender = () => {
    if (sizeLimit) {
      const limitedArray = products.slice(0, sizeLimit);
      return limitedArray;
    } else {
      return products;
    }
  };

  useEffect(() => {
    const handleRouterChange = () => {
      trackedProductIds.clear();
      productTrackingEntries = [];
    };
    router.events.on('routeChangeStart', handleRouterChange);

    return () => {
      router.events.off('routeChangeStart', handleRouterChange);
    };
  }, [router]);

  useEffect(() => {
    const saveScroll = () => {
      const y = window.scrollY;

      const history = window.history.state;
      const options = { ...history.options, scrollY: y };

      window.history.replaceState({ ...history, options }, '');
    };

    document.addEventListener('click', saveScroll, true);

    if (window.history.state && window.history.state.options.scrollY) {
      const savedY = window.history.state.options.scrollY;

      window.scrollTo(0, savedY);
    }

    return () => {
      document.removeEventListener('click', saveScroll, true);
    };
  }, []);

  useEffect(() => {
    if (products.length <= 10) {
      dispatch(
        updateSessionDetail({
          name: 'grid_recs_eligibility',
          value: ' ',
        })
      );
    }
  }, [dispatch, loading, products.length]);

  const renderItem = (item, index) => {
    let isTeaser = false;
    let shouldShowProducts = true;
    let fplId = null;

    if (asset && asset.options) {
      const {
        options: {
          customVars: { flags, countDownEndDate, shouldHideFpl, fpl },
        },
      } = asset;

      fplId = fpl;

      if (flags && flags.startsWith('modal')) {
        if (getNow() < parseDate(countDownEndDate)) {
          isTeaser = true;
        } else {
          isTeaser = false;
        }
      }
      if (shouldHideFpl) {
        shouldShowProducts = false;
      }
    }

    const gridPosition = index + 1;

    const extraQueryParams = {
      gridPosition,
      psrc: pSource,
    };

    return (
      (item.isAsset && item.element) ||
      (shouldShowProducts && (
        <ProductCell
          renderSmall={item.render_small}
          productTrackingEntries={productTrackingEntries}
          extraQueryParams={extraQueryParams}
          filters={filters}
          fplId={filters?.fpls || fplId}
          gridPosition={gridPosition}
          hidePromo={hidePromo}
          hoverable={hoverable}
          isOnGrid
          isOnSearchGrid={isOnSearchGrid}
          isTeaser={isTeaser}
          key={`${item.master_product_id}_${index}`}
          pSource={pSource}
          product={item}
          searchQuery={searchQuery}
          sortBy={sortBy}
          trackedProductIds={trackedProductIds}
        />
      )) ||
      null
    );
  };

  const renderProducts = () => {
    const products = productsToRender() || [];

    if (assetSlots && products.length) {
      Object.keys(assetSlots).forEach(index => {
        const indexNumber = parseInt(index, 10);
        const element = assetSlots[index];
        const asset = {
          isAsset: true,
          element,
        };
        // make the 4 elements following a 2-col carousel smaller
        if (element?.props && element.props[`data-span`] === 2) {
          for (let i = indexNumber; i < indexNumber + 4; i++) {
            if (products[i]) {
              products[i].render_small = true;
            }
          }
        }

        products.splice(indexNumber, 0, asset);
      });
    }

    return products.map(renderItem);
  };

  return (
    <Grid className={className} breakpoints={breakpoints}>
      {renderProducts()}
      {children}
    </Grid>
  );
};

ProductGrid.propTypes = {
  asset: PropTypes.object,
  assetSlots: PropTypes.object,
  breakpoints: PropTypes.array,
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * Whether products rendered in the grid will display `hoverable` features.
   * If not, the grid will be rendered in a more compact fashion.
   */
  hoverable: PropTypes.bool,
  filters: PropTypes.object,
  hidePromo: PropTypes.bool,
  isOnSearchGrid: PropTypes.bool,
  products: PropTypes.array,
  searchQuery: PropTypes.string,
  pSource: PropTypes.string,
  loading: PropTypes.bool,
  sizeLimit: PropTypes.number,
  sortBy: PropTypes.string,
};

export default ProductGrid;
